/**
 * Dropping `undefined` before a Firestore write.
 *
 * WHY THIS EXISTS. The web SDK is initialised without
 * `ignoreUndefinedProperties` (see src/firebase/client.ts), so a single
 * `undefined` ANYWHERE in a payload makes the whole `updateDoc` throw
 * "Unsupported field value: undefined" and the seller's entire save is lost,
 * not just the offending field. The product forms are two hand-maintained
 * 3000-line pages that build their payloads by spreading React state, and a
 * key parked as `undefined` in state to mean "this product has no stored
 * original" is a completely ordinary thing for them to do. Requiring every one
 * of those spreads to be individually undefined-safe is a rule that will be
 * broken again the next time either page grows a field.
 *
 * `undefined` is NOT how Firestore deletes a field, which is what makes this
 * safe to do centrally: `deleteField()` is the sentinel for that, and it is an
 * object this function passes through untouched. So a dropped key means
 * "leave whatever is stored alone", which is exactly what the callers that
 * park an `undefined` already intend.
 */

/**
 * Whether a value is a plain object we may safely rebuild.
 *
 * Anything with a prototype other than Object.prototype (or none) is passed
 * through BY REFERENCE rather than copied. That is the important half: Date,
 * Timestamp, GeoPoint, DocumentReference and the FieldValue sentinels
 * (serverTimestamp, deleteField, arrayUnion, increment) are all class
 * instances, and rebuilding one as a plain object would strip the identity the
 * SDK dispatches on and change the meaning of the write.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * A copy of `value` with every `undefined` property removed, recursively.
 *
 * Array ELEMENTS that are `undefined` become `null` rather than being dropped.
 * Removing them would shift every later element down an index, and these
 * arrays are ordered galleries and price tiers where position is meaning; a
 * hole is a bug to see in the data, not one to silently paper over by
 * renumbering the seller's images.
 */
export function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => (item === undefined ? null : stripUndefinedDeep(item))) as unknown as T;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      if (item === undefined) continue;
      out[key] = stripUndefinedDeep(item);
    }
    return out as unknown as T;
  }
  return value;
}
